import { useEffect } from 'react';
import Widget from 'components/Dashboard/Widget';
import { useKPIGetBookedToChanges } from 'api/kpi/kpi';
import { KPIsWidgetEnum, LongTitles } from '../../utils';
import useManagementKpiFilter from './useManagementKpiFilter';
import {
  ControlFigures,
  nf,
  pct,
  tableStyle,
  tdNumStyle,
  tdStyle,
  thNumStyle,
  thStyle,
  totalRowStyle,
  WidgetState,
} from './shared';

/** Samma ordning som ledningsrapportens panel: ansvar, sedan orsakerna i admins ordning. */
const RESPONSIBILITY_ORDER = ['Kund', 'Riddermark', 'Zäkra', 'Övrigt'];

/**
 * Ändrade bokningstider — hur ofta ett befintligt uppdrags bokat till-tid flyttas, per ansvar
 * och orsak. Räknas på dagen ändringen gjordes.
 */
const BookedToChangesWidget = () => {
  const { isReady, isDateRangeTooLong, filter, deps } =
    useManagementKpiFilter();
  const getData = useKPIGetBookedToChanges();

  useEffect(() => {
    if (isReady) {
      getData.mutate({ data: filter });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const result = getData.data;
  const total = result?.totalChanges ?? 0;

  const rows = new Map<string, { responsibility: string; changes: number }>();
  (result?.dataPoints ?? []).forEach((dp) => {
    const row = rows.get(dp.reasonName) ?? {
      responsibility: dp.responsibilityName,
      changes: 0,
    };
    row.changes += dp.changes;
    rows.set(dp.reasonName, row);
  });
  const reasons = Array.from(rows.entries())
    .map(([name, row]) => ({ name, ...row }))
    .sort(
      (a, b) =>
        RESPONSIBILITY_ORDER.indexOf(a.responsibility) -
          RESPONSIBILITY_ORDER.indexOf(b.responsibility) ||
        b.changes - a.changes
    );

  return (
    <Widget
      title={LongTitles[KPIsWidgetEnum.BookedToChanges]}
      count={total}
      isLoading={getData.isLoading}
      info="Manuella flyttar av bokat till-tiden på kundnära uppdrag, per orsak. Räknas på dagen ändringen gjordes."
    >
      <WidgetState
        isLoading={getData.isLoading}
        isDateRangeTooLong={isDateRangeTooLong}
        isEmpty={reasons.length === 0}
      >
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Ansvar</th>
              <th style={thStyle}>Orsak</th>
              <th style={thNumStyle}>Antal</th>
              <th style={thNumStyle}>Andel</th>
            </tr>
          </thead>
          <tbody>
            {reasons.map((reason) => (
              <tr key={reason.name} style={{ borderBottom: '1px solid #eee' }}>
                <td style={tdStyle}>{reason.responsibility}</td>
                <td style={tdStyle}>{reason.name}</td>
                <td style={tdNumStyle}>{nf(reason.changes)}</td>
                <td style={tdNumStyle}>
                  {total > 0
                    ? `${nf((reason.changes / total) * 100, 1)} %`
                    : '–'}
                </td>
              </tr>
            ))}
            <tr style={totalRowStyle}>
              <td style={tdStyle} colSpan={2}>
                Totalt
              </td>
              <td style={tdNumStyle}>{nf(total)}</td>
              <td style={tdNumStyle}>
                {nf(result?.totalAssignments ?? 0)} uppdrag
              </td>
            </tr>
          </tbody>
        </table>

        <table style={{ ...tableStyle, marginTop: '16px' }}>
          <thead>
            <tr>
              <th style={thStyle}>Ärendetyp</th>
              <th style={thNumStyle}>Ombokningar</th>
              <th style={thNumStyle}>Uppdrag</th>
              <th style={thNumStyle}>Ombokningsgrad</th>
            </tr>
          </thead>
          <tbody>
            {(result?.byCaseType ?? []).map((row) => (
              <tr
                key={row.key ?? row.name}
                style={{
                  borderBottom: '1px solid #eee',
                  color: row.isCustomerFacing ? undefined : '#666',
                }}
              >
                <td style={tdStyle}>
                  {row.name}
                  {!row.isCustomerFacing && ' *'}
                </td>
                <td style={tdNumStyle}>{nf(row.changes)}</td>
                <td style={tdNumStyle}>{nf(row.assignments)}</td>
                <td style={tdNumStyle}>{pct(row.rebookingRatePercentage)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <table style={{ ...tableStyle, marginTop: '16px' }}>
          <thead>
            <tr>
              <th style={thStyle}>Kundens län</th>
              <th style={thNumStyle}>Ombokningar</th>
              <th style={thNumStyle}>Kundnära uppdrag</th>
              <th style={thNumStyle}>Ombokningsgrad</th>
            </tr>
          </thead>
          <tbody>
            {(result?.byCounty ?? []).map((row) => (
              <tr key={row.name} style={{ borderBottom: '1px solid #eee' }}>
                <td style={tdStyle}>{row.name}</td>
                <td style={tdNumStyle}>{nf(row.changes)}</td>
                <td style={tdNumStyle}>{nf(row.assignmentsInPeriod)}</td>
                <td style={tdNumStyle}>{pct(row.rebookingRatePercentage)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <ControlFigures
          items={[
            '* manuella flyttar på ärendetyper utan kundlöfte — jämförelse, ingår inte i totalen',
            'ombokningsgrad = andel av kundnära uppdrag med bokad tid i perioden som ombokats',
            `${nf(result?.corrections ?? 0)} rättningar av felinlagd tid räknas inte`,
            `${nf(result?.nonCustomerFacingChanges ?? 0)} ändringar på uppdrag utan kundlöfte (t.ex. Annons) räknas inte`,
            `${nf(result?.systemChanges ?? 0)} automatiska ändringar räknas inte`,
            `${nf(result?.withoutReason ?? 0)} ombokningar saknar orsak`,
          ]}
        />
      </WidgetState>
    </Widget>
  );
};

export default BookedToChangesWidget;
